/**
 * User Feedback Service for submitting product feedback from the AI area
 */

import { defaultErrorHandler as commonErrorHandler, createAbortSafeErrorHandler, createHeaders, getApiUrls } from './serviceConfig';

import type { SubmitFeedbackOptions, SubmitFeedbackPayload } from 'app/types/feedback';
import { feedbackUploadRequest } from './feedbackUploadRequest';
export type { FeedbackCategory, SubmitFeedbackOptions, SubmitFeedbackPayload } from 'app/types/feedback';

type FeedbackServiceConfig = {
  onError?: (error: unknown) => void;
};

type FeedbackService = {
  submitFeedback: (payload: SubmitFeedbackPayload, options?: SubmitFeedbackOptions) => Promise<void>;
};

/**
 * Creates a configured feedback service
 */
export function createFeedbackService(config: FeedbackServiceConfig = {}): FeedbackService {
  const configuredErrorHandler = config.onError || ((error: unknown) => commonErrorHandler('Feedback service', error as Error));

  const defaultErrorHandler = createAbortSafeErrorHandler(configuredErrorHandler) as (error: unknown) => void;

  const submitFeedback = async (
    { category, comment, attachments = [] }: SubmitFeedbackPayload,
    { signal, onProgress }: SubmitFeedbackOptions = {}
  ): Promise<void> => {
    try {
      const urls = getApiUrls();
      if (attachments.length > 0) {
        const body = new FormData();
        body.append('category', category);
        body.append('comment', comment);
        attachments.forEach((file) => body.append('attachments', file, file.name));
        await feedbackUploadRequest({ url: urls.feedback.submit(), body, headers: createHeaders(), signal, onProgress });
        return;
      }
      const response = await fetch(urls.feedback.submit(), {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify({ category, comment }),
        signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to submit feedback: ${response.status}`);
      }
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  return { submitFeedback };
}

export default createFeedbackService();
